import { watch, ComputedRef, inject } from 'vue';
import { toDataPath, JsonFormsSubStates, UISchemaElement } from '@jsonforms/core';
import { useErrors } from './useErrors';
import { resourceMakeApiCall } from '@/pages/registrations/api/api';
import { AutoSetCondition, AutoSetResourceSetTo, CommonJsonFormsControl } from './types';

/** Public alias kept for backwards compatibility with existing imports of this config shape. */
// export type AutoSetOnChangeConfig = AutoSetOnChangeOptions;

/** Narrows a condition's `setTo` to the resource-driven shape (as opposed to a literal value). */
function isResourceSetTo(setTo: AutoSetCondition['setTo']): setTo is AutoSetResourceSetTo {
  return typeof setTo === 'object' && setTo !== null && 'resource' in setTo;
}

/**
 * Recursively walks the uiSchema tree looking for the element whose `scope` matches.
 * Used to patch that element's `options` at runtime (e.g. mark a field read-only once an
 * autoSetOnChange resource fetch resolves).
 */
function findUiSchemaElementByScope(element: UISchemaElement, scope: string): UISchemaElement | undefined {
  // Base case: this element's own scope is the one we're looking for.
  if ('scope' in element && (element as any).scope === scope) {
    return element;
  }
  // Recursive case: this is a layout (Group/HorizontalLayout/...) — search its children.
  if ('elements' in element && Array.isArray((element as any).elements)) {
    for (const child of (element as any).elements as UISchemaElement[]) {
      const found = findUiSchemaElementByScope(child, scope);
      // Stop as soon as one of the children (or their descendants) matches.
      if (found) return found;
    }
  }
  // Not present anywhere in this branch of the tree.
  return undefined;
}

/**
 * Composable for Control renderers to handle auto-set field updates.
 * Watches a dependent field and directly updates other fields based on conditions.
 *
 * Configuration in uiSchema — literal value, set port when protocol is "icmp":
 * ```json
 * {
 *   "type": "Control",
 *   "scope": "#/properties/protocol",
 *   "options": {
 *     "autoSetOnChange": {
 *       "conditions": [{ "setTo": "0,8", "value": "icmp" }],
 *       "updateField": "#/properties/port"
 *     }
 *   }
 * }
 * ```
 *
 * Configuration in uiSchema — resource-driven value, fetch intranet IPs from an API and mark
 * the target field read-only once set:
 * ```json
 * {
 *   "type": "Control",
 *   "scope": "#/properties/intranetIPs",
 *   "options": {
 *     "autoSetOnChange": {
 *       "conditions": [{
 *         "value": true,
 *         "setTo": {
 *           "options": { "readOnly": true },
 *           "resource": {
 *             "data": {},
 *             "apiCallId": "GET-SERVICE-MNGMT-NETWORK-INTRANET-IP",
 *             "infraServiceApmId": "NETWORK"
 *           }
 *         }
 *       }],
 *       "updateField": "#/properties/source"
 *     }
 *   }
 * }
 * ```
 *
 * Usage in renderer setup():
 * useAutoSetOnChange(control, handleChange);
 */
export const useAutoSetOnChange = (
  control: ComputedRef<CommonJsonFormsControl>,
  handleChange?: (path: string, value: unknown) => void,
): void => {
  const autoSetOnChange = control.value.uischema?.options?.autoSetOnChange;
  // Nothing configured for this control — skip entirely, no watcher registered.
  if (!autoSetOnChange) return;

  const jsonforms = inject<JsonFormsSubStates>('jsonforms');

  const { updateField, conditions } = autoSetOnChange;
  const { removeErrorsBySource } = useErrors();
  watch(
    () => control.value.data,
    async (newWatchedValue) => {
      // Ignore the initial/cleared state; only react once the watched field has a real value.
      if (newWatchedValue === undefined || newWatchedValue === null) {
        return;
      }

      // Find matching condition for the watched field's current value
      const matchedCondition: AutoSetCondition | undefined = conditions.find((cond) => cond.value === newWatchedValue);

      // Directly set the dependent field value if condition matched
      if (matchedCondition !== undefined && handleChange) {
        if (isResourceSetTo(matchedCondition.setTo)) {
          // Resource-driven setTo: call the backend API instead of writing a literal value.
          const data = await resourceMakeApiCall({
            infraServiceApmId: matchedCondition.setTo.resource.infraServiceApmId,
            apiCallId: matchedCondition.setTo.resource.apiCallId,
            data: matchedCondition.setTo.resource.data,
          });
          handleChange(toDataPath(updateField), data.getResourceResponse);
          removeErrorsBySource(toDataPath(updateField) ?? 'useAutoSetOnChange');
          // If the condition also carries extra uiSchema `options` (e.g. { readOnly: true }),
          // merge them onto the target element so the UI reflects the auto-set state.
          if (matchedCondition.setTo.options && jsonforms?.core?.uischema) {
            const element = findUiSchemaElementByScope(jsonforms.core.uischema, updateField);
            if (element) {
              (element as any).options = { ...(element as any).options, ...matchedCondition.setTo.options };
            }
          }
        } else {
          // Literal setTo: write the configured primitive value straight to the target field.
          handleChange(toDataPath(updateField), matchedCondition.setTo);
        }
      } else {
        // No condition matched the watched field's value (or handleChange wasn't provided) —
        // undo any uiSchema options previously merged in by a resource-driven condition, so the
        // target field returns to its default (e.g. no longer forced read-only).
        const element = jsonforms?.core?.uischema && findUiSchemaElementByScope(jsonforms.core.uischema, updateField);
        if (element && (element as any).options) {
          const keysToRemove = conditions.flatMap((cond) =>
            isResourceSetTo(cond.setTo) ? Object.keys(cond.setTo.options ?? {}) : [],
          );
          // practically here we are removing the options that were added by the resource-driven condition like [readOnly]
          for (const key of keysToRemove) {
            delete (element as any).options[key];
          }
        }
      }
    },
  );
};
